import { ApiError } from './api';
import { SESSION_PROTOCOL_VERSION } from './session-core';

export type CareScheduleType = 'once' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'interval';
export interface CareScheduleSummary {
  id: string;
  message: string;
  scheduleType: CareScheduleType;
  timezone: string;
  localDate: string;
  localTime: string;
  intervalHours: number | null;
  weekdays: number[] | null;
  windowStart: string | null;
  windowEnd: string | null;
  status: 'active' | 'delivered' | 'cancelled' | 'delivery_failed' | 'suppressed';
  nextDeliveryAt: string | null;
  deliveryHealth: 'ready' | 'retrying';
  createdAt: string;
}
export interface CareState {
  activeSchedules: CareScheduleSummary[];
  receivingMuted: boolean;
  activeLimit: number;
}

const request = async <T>(path: string, initData: string, options?: RequestInit): Promise<T> => {
  const response = await fetch(path, {
    ...options,
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
      Authorization: `tma ${initData}`,
      'Content-Type': 'application/json',
      'X-UTAGE-Protocol': String(SESSION_PROTOCOL_VERSION),
      ...options?.headers
    }
  });
  const body = await response.json().catch(() => ({})) as T & { error?: { code?: string; message?: string } };
  if (!response.ok) throw new ApiError(body.error?.message ?? 'Could not update Care.', body.error?.code);
  return body;
};

export const loadCareState = async (initData: string, connectedUserId: string): Promise<CareState> => {
  const result = await request<{ state: CareState }>(`/care/${encodeURIComponent(connectedUserId)}`, initData);
  return result.state;
};

export const createCareSchedule = async (initData: string, connectedUserId: string, input: {
  actionId: string;
  message: string;
  scheduleType: CareScheduleType;
  timezone: string;
  localDate: string;
  localTime: string;
  intervalHours?: number | null;
  weekdays?: number[] | null;
  windowStart?: string | null;
  windowEnd?: string | null;
}): Promise<CareState> => {
  const result = await request<{ state: CareState }>(`/care/${encodeURIComponent(connectedUserId)}/schedules`, initData, {
    method: 'POST', body: JSON.stringify(input)
  });
  return result.state;
};

export const cancelCareSchedule = async (initData: string, connectedUserId: string, scheduleId: string, actionId: string): Promise<CareState> => {
  const result = await request<{ state: CareState }>(`/care/${encodeURIComponent(connectedUserId)}/schedules/${encodeURIComponent(scheduleId)}/cancel`, initData, {
    method: 'POST', body: JSON.stringify({ actionId })
  });
  return result.state;
};

export const updateCarePreference = async (initData: string, connectedUserId: string, muted: boolean, actionId: string): Promise<CareState> => {
  const result = await request<{ state: CareState }>(`/care/${encodeURIComponent(connectedUserId)}/preference`, initData, {
    method: 'PUT', body: JSON.stringify({ muted, actionId })
  });
  return result.state;
};
